import { Ref } from '@wix/materializer'
import { BaseDataItem, DataMapName } from '@wix/thunderbolt-becky-types'
import { DataPlanner } from '../../types'
import { createBeckyRef } from '../../utils'
import allSchemas, { Schema } from './dataSchemas'
import { dataPlanners } from './typeResolvers'

const schemas = { ...allSchemas, theme_data: {} } as typeof allSchemas
export const resolveDataItem = <T extends Record<string, any> & BaseDataItem<any>>(
	dataItem: T,
	schema: Schema<any>,
	getRef: (id: string, isWeakRef: boolean) => Ref<any>
): T => {
	const refs = Object.keys(schema).reduce((acc, key) => {
		if (!dataItem[key]) {
			return acc
		}

		const innerSchema = schema[key]
		if (typeof innerSchema === 'object') {
			return { ...acc, [key]: resolveDataItem(dataItem[key], innerSchema, getRef) }
		}

		const isWeakRef = innerSchema
		return {
			...acc,
			[key]: Array.isArray(dataItem[key])
				? dataItem[key].map((ref: string) => getRef(ref.replace('#', ''), isWeakRef))
				: getRef(dataItem[key].replace('#', ''), isWeakRef),
		}
	}, {} as T)

	return { ...dataItem, ...refs }
}

type DraftDataItem = BaseDataItem<any> & {
	_draftRefs?: Array<{ path: Array<string>; jsonPointer: string }>
	_isDraftItem: boolean
}

const createDraftSchema = (draftRefs: DraftDataItem['_draftRefs'] = []) =>
	draftRefs.reduce((acc, { path }) => {
		acc[path[0]] = false
		return acc
	}, {} as Record<string, boolean>)

const isDraftDataItem = (dataItem: any): dataItem is DraftDataItem => dataItem.hasOwnProperty('_isDraftItem')

export const planner: DataPlanner<Record<DataMapName, Record<string, BaseDataItem<any>>>> = {
	namespace: 'data',
	onUpdate: (dataMapName, id, dataItem) => {
		if (!dataItem) {
			return {
				[dataMapName]: { [id]: undefined },
			}
		}

		const schema = isDraftDataItem(dataItem)
			? { ...schemas[dataMapName]?.[dataItem.type], ...createDraftSchema(dataItem._draftRefs) }
			: schemas[dataMapName]?.[dataItem.type]

		const result = schema
			? resolveDataItem(dataItem, schema, (dataId: string, isWeakRef: boolean = false) =>
					createBeckyRef(planner, [dataMapName, dataId], isWeakRef)
			  )
			: dataItem

		const specificDataItemMapper = dataPlanners[result.type]
		return {
			[dataMapName]: { [id]: specificDataItemMapper?.plan(result) ?? result },
		}
	},
	toViewItem: (resolvedData) => {
		const specificDataItemTransform = dataPlanners[resolvedData.type]
		return specificDataItemTransform?.toViewItem(resolvedData) ?? resolvedData
	},
}
